/**
 * Période sélectionnée, stockée dans l'URL.
 *
 * Trois raisons de la mettre là plutôt que dans un état React :
 *   · elle survit à la navigation entre les onglets — changer de section ne
 *     doit pas réinitialiser la période qu'on vient de choisir ;
 *   · l'URL devient partageable : « regarde les 90 derniers jours » est un lien ;
 *   · le rechargement de la page conserve le contexte.
 *
 * Et c'est zéro bibliothèque de plus : React Router expose déjà la query string.
 *
 * ── La borne haute est rabattue sur le dernier jour de données ───────────────
 * Hier pour l'usage de l'app (sync nocturne), aujourd'hui pour le reste
 * (cf. `DataScope` dans `lib/dates.ts`, déduit de l'adresse de la page). Une
 * URL partagée la semaine dernière porte une borne haute désormais dépassée :
 * on la ramène dessus au lieu d'ajouter une journée vide au bout de la courbe.
 */

import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { clampToLatestDay, clampToSelectable, latestDataDay } from '../lib/dates';
import { clampDay, periodRulesForPath } from '../lib/periodRules';
import type { SelectedPeriod } from '../types';
import { useParisDay } from './useParisDay';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function usePeriodParam(): [SelectedPeriod, (next: SelectedPeriod) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const today = useParisDay();

  const period = useMemo<SelectedPeriod>(() => {
    // Préréglages, bornes et période par défaut de la page (BF 26 : 1er nov. →
    // 31 déc., hier par défaut ; cf. `lib/periodRules`).
    const rules = periodRulesForPath(pathname);
    const { scope, bounds } = rules;

    // Un préréglage est relatif (« 7 jours », « Aujourd'hui ») : on recalcule
    // ses bornes au lieu de croire celles de l'URL, écrites le jour du clic.
    // Sans ça, la web app reprise le lendemain afficherait encore la veille.
    // Un préréglage d'une autre page (« Mois en cours » arrivé du Résumé sur
    // BF 26) ne vaut pas ici : période par défaut de la page.
    const presetId = searchParams.get('preset');
    const preset = presetId ? rules.presets.find((p) => p.id === presetId) : undefined;
    if (preset) return { ...preset.range(scope), presetId: preset.id };
    if (presetId && bounds) return rules.defaultPeriod();

    const from = searchParams.get('from');
    const to = searchParams.get('to');

    // Une URL bricolee a la main ne doit pas casser la page : on retombe sur
    // la periode par defaut plutot que d'envoyer des dates invalides a l'API.
    if (!from || !to || !ISO_DATE.test(from) || !ISO_DATE.test(to) || from > to) {
      return rules.defaultPeriod();
    }

    // Les données s'arrêtent au dernier jour de la portée (cf. `latestDataDay`).
    // Une URL partagée la semaine dernière, ou un signet, porte une borne haute
    // désormais dépassée qui ajouterait une journée vide au bout de la courbe.
    // La fin peut aller jusqu'au dernier jour du mois en cours (période
    // pilotée), le début pas au-delà du dernier jour de données : une période
    // qui ne contiendrait aucun jour de données n'a rien à afficher.
    let clampedTo = bounds ? to : clampToSelectable(to);
    let clampedFrom = clampToLatestDay(from, scope);
    // Page bornée : une période qui en sort est ramenée dedans ; entièrement
    // dehors, période par défaut.
    if (bounds) {
      if (to < bounds.min || from > bounds.max) return rules.defaultPeriod();
      clampedFrom = clampDay(clampedFrom, bounds.min, bounds.maxStart);
      clampedTo = clampDay(clampedTo, bounds.min, bounds.max);
    }

    // `from` entièrement dans le futur : il ne reste rien à afficher, et
    // rabattre les deux bornes ferait passer une période d'un jour pour un
    // choix de l'utilisateur.
    if (clampedFrom > clampedTo || clampedFrom > latestDataDay(scope)) {
      return rules.defaultPeriod();
    }

    return { from: clampedFrom, to: clampedTo, presetId: null };
    // `today` ne sert qu'à recalculer au changement de jour.
  }, [searchParams, pathname, today]);

  const setPeriod = useCallback(
    (next: SelectedPeriod) => {
      const params: Record<string, string> = { from: next.from, to: next.to };
      if (next.presetId) params.preset = next.presetId;
      // `replace` : chaque clic sur un preset ne doit pas empiler une entree
      // dans l'historique, sinon le bouton « precedent » remonte les periodes
      // une par une au lieu de quitter la page.
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  return [period, setPeriod];
}
